import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import SchemaForm from '../../SchemaForm';
import Select from '../../Select';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import { actions as actionsApi } from '@afosto/content/api/Actions';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import camelize from '@afosto/utils/string/camelize';
import TextField from '../../TextField';
import getSchemas from './getSchemas';
import stripWhitespaces from '@afosto/utils/string/stripWhitespaces';
import translations from './translations';

const BatchActionForm = ({
  action: providedAction,
  batchReference: providedBatchReference,
  code: providedCode,
  config: providedConfig,
  excludeActions,
  lockAction,
  onCancel,
  onSubmit,
  renderActions,
}) => {
  const intl = useIntl();
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState(providedAction);
  const [actionError, setActionError] = useState(false);
  const [isFetchingActions, setIsFetchingActions] = useState(true);
  const [batchReference, setBatchReference] = useState(providedBatchReference);
  const [batchReferenceError, setBatchReferenceError] = useState(false);
  const [schema, setSchema] = useState({});
  const [uiSchema, setUiSchema] = useState({});

  useEffect(() => {
    let isMounted = true;

    const fetchActions = async () => {
      setIsFetchingActions(true);

      try {
        const response = await actionsApi.listActions();
        let availableActions = response?.data ?? [];

        if (providedCode) {
          availableActions = availableActions.filter(({ code }) => code === providedCode);
        }

        const formattedActions = availableActions
          .filter(availableAction => !excludeActions.includes(availableAction?.id))
          .reduce((acc, result) => {
            const { description, ...otherValues } = result;
            const label = intl.formatMessage({
              id: `batchActions.${camelize(result.id)}`,
              defaultMessage: `${description}`,
              description: `The translation of batch action ${result.id}`,
            });
            return [...acc, { ...otherValues, value: result.id, label }];
          }, []);

        if (isMounted) {
          setActions(formattedActions);
        }
      } catch (err) {
        if (isMounted) {
          setActions([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingActions(false);
        }
      }
    };

    fetchActions();

    return () => {
      isMounted = false;
    };
  }, [intl, providedCode, excludeActions]);

  useEffect(() => {
    let isMounted = true;

    if (action) {
      const { action: actionSchema = {}, ui: actionUiSchema = {} } = getSchemas(intl, action?.id);

      if (isMounted) {
        setSchema(actionSchema);
        setUiSchema(actionUiSchema);
      }
    } else if (isMounted) {
      setSchema({});
      setUiSchema({});
    }

    return () => {
      isMounted = false;
    };
  }, [intl, action]);

  const handleSubmit = async formConfiguration => {
    try {
      let hasErrors = false;

      if (!batchReference || stripWhitespaces(batchReference) === '') {
        setBatchReferenceError(true);
        hasErrors = true;
      } else {
        setBatchReference(false);
      }

      if (!action) {
        setActionError(true);
        hasErrors = true;
      } else {
        setActionError(false);
      }

      if (hasErrors) {
        return Promise.reject(new Error('Missing required fields'));
      }

      const data = {
        action,
        batchReference,
        config: formConfiguration,
      };

      if (onSubmit && typeof onSubmit === 'function') {
        await onSubmit(data);
      }

      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  return (
    <LoadingWrapper isLoading={isFetchingActions}>
      <TextField
        name="batchReference"
        label={intl.formatMessage(translations.fields.batchReference.label)}
        onChange={(event, newValue) => setBatchReference(newValue)}
        value={batchReference ?? ''}
        register={false}
        error={batchReferenceError}
        errorMessage={intl.formatMessage(
          {
            id: 'errors.validation.required',
            defaultMessage: '{target} is required',
            description: 'Error message of the batch reference field in the batch form',
          },
          { target: intl.formatMessage(translations.fields.batchReference.label) },
        )}
      />

      <Select
        name="action"
        label={intl.formatMessage(translations.fields.action.label)}
        suggestions={actions}
        onChange={(event, newValue) => setAction(findObjectInArray(actions, 'id', newValue))}
        value={action?.id ?? ''}
        register={false}
        inline={lockAction}
        readOnly={lockAction}
        error={actionError}
        errorMessage={intl.formatMessage(
          {
            id: 'errors.validation.required',
            defaultMessage: '{target} is required',
            description: 'Error message of the action field in the batch form',
          },
          { target: intl.formatMessage(translations.fields.action.label) },
        )}
      />

      <SchemaForm
        schema={schema}
        uiSchema={uiSchema}
        values={providedConfig}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        renderActions={renderActions}
      />
    </LoadingWrapper>
  );
};

BatchActionForm.propTypes = {
  action: PropTypes.object,
  batchReference: PropTypes.string,
  code: PropTypes.string,
  config: PropTypes.object,
  excludeActions: PropTypes.array,
  lockAction: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  renderActions: PropTypes.bool,
};

BatchActionForm.defaultProps = {
  action: undefined,
  batchReference: '',
  code: undefined,
  config: {},
  excludeActions: [],
  lockAction: false,
  onCancel: undefined,
  onSubmit: undefined,
  renderActions: true,
};

export default BatchActionForm;
